import { createContext, Dispatch, JSX, SetStateAction, useState, useContext, useEffect, useMemo } from "react";
import { InvalidItemsTableData, ResultsSummaryTableData, ReviewProjectsTableData, BulkProviderChangeProjectTableData } from "@app/shared/provider-change-wizards/interfaces";
import ProjectionProviderService from "@app/projects/projection-provider/projectionProviderService";
import { reportName } from "@app/exports/export-panel-body/exportPanelBodyHelpers";
import ProjectService from "@app/projects/projectService";
import { BulkProjectTypeEnum, BulkTargetingWizardSteps, BulkTargetExistingTargetActionEnum, ColumnConfigurationReport } from "@app/targeting/import/interfaces";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { getTargetingReportSections } from "@app/targeting/import/reportSections";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";

/* oxlint-disable sort-keys */
interface BulkTargetingWizardContextProps {
  projectService: ProjectService;
  projectionProviderService: ProjectionProviderService;
  availableReportSectionKeys: string[];
  projectType: BulkProjectTypeEnum;

  bulkProviderTargetId: number;
  bulkImportFormData: FormData;
  comment: string;
  emailOnSuccess: boolean;
  filename: string;
  formatType: string;
  invalidItems: InvalidItemsTableData[];
  projects: BulkProviderChangeProjectTableData[];
  resultsSummary: ResultsSummaryTableData[];
  reviewProjects: ReviewProjectsTableData[];
  page: BulkTargetingWizardSteps;
  // The ability to select projects by tag means that disabled projects can be selected indirectly.
  // We need to keep track of the total selected projects to make the tags UI behave as expected 
  // (e.g. clicking tag checkbox where the tag includes disabled projects still checks the box),
  // and also the eligible selections for validation and sending with the API request
  eligibleSelectedProjectIds: string[];
  totalSelectedProjectIds: string[];
  targetingReason: string;
  existingTargetAction: BulkTargetExistingTargetActionEnum;
  isDistinctServicingProviderReport: boolean;
  includeReportMetadataSummary: boolean;
  selectedReports: string[];
  columnConfigurations: { [reportType: string]: ColumnConfigurationReport };
  // reportPresets are keyed on ReportType (not section key) because merged Service Area/Opportunity sections
  // share a report type and must share the same preset list, so creating a preset on one side updates the other.
  reportPresets: Partial<Record<ReportType, ColumnConfigurationPresetOption[]>>;

  setBulkProviderTargetId: (id: number) => void;
  setBulkImportFormData: (formData: FormData) => void;
  setComment: (newComment: string) => void;
  setEmailOnSuccess: (flag: boolean) => void;
  setFormatType: (newType: string) => void;
  setFilename: (newFilename: string) => void;
  setInvalidItems: (invalidItems: InvalidItemsTableData[]) => void;
  setPage: (newPage: BulkTargetingWizardSteps) => void;
  setProjects: (projects: BulkProviderChangeProjectTableData[]) => void;
  setResultsSummary: (results: ResultsSummaryTableData[]) => void;
  setReviewProjects: (projects: ReviewProjectsTableData[]) => void;
  setEligibleSelectedProjectIds: Dispatch<SetStateAction<string[]>>;
  setTotalSelectedProjectIds: Dispatch<SetStateAction<string[]>>;
  setTargetingReason: (targetingReason: string) => void;
  setExistingTargetAction: (action: BulkTargetExistingTargetActionEnum) => void;
  setIsDistinctServicingProviderReport: (isDistinct: boolean) => void;
  setIncludeReportMetadataSummary: (flag: boolean) => void;
  setSelectedReports: (reports: string[]) => void;
  setColumnConfigurations: (configs: { [reportType: string]: ColumnConfigurationReport }) => void;
  setReportPresets: (reportType: ReportType, presets: ColumnConfigurationPresetOption[]) => void;
}

const BulkTargetingWizardContext = createContext<Partial<BulkTargetingWizardContextProps>>({
  comment: "",
  eligibleSelectedProjectIds: [],
  emailOnSuccess: true,
  filename: "",
  formatType: "CSV",
  invalidItems: [],
  page: BulkTargetingWizardSteps.SelectProjects,
  projectType: BulkProjectTypeEnum.County,
  projects: [],
  resultsSummary: [],
  reviewProjects: [],
  totalSelectedProjectIds: [],
  targetingReason: "",
  isDistinctServicingProviderReport: false,
  includeReportMetadataSummary: false,
  selectedReports: [],
  columnConfigurations: {},
  reportPresets: {},
  availableReportSectionKeys: []
});

const BulkTargetingWizardProvider = (props: Partial<BulkTargetingWizardContextProps> & { children: React.ReactNode }): JSX.Element => {
  const { projectType = BulkProjectTypeEnum.County } = props;
  const projectService = new ProjectService();
  const projectionProviderService = new ProjectionProviderService();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const isVolumeTargetingReportConfigurationEnabled = featureFlags.has(Features.VOLUME_TARGETING_REPORT_CONFIGURATION);

  const [bulkImportFormData, setBulkImportFormData] = useState<FormData>();
  const [comment, setComment] = useState("");
  const [emailOnSuccess, setEmailOnSuccess] = useState(true);
  const [filename, setFilename] = useState(reportName("VolumeTargeting"));
  const [formatType, setFormatType] = useState("CSV");
  const [invalidItems, setInvalidItems] = useState<InvalidItemsTableData[]>([]);
  const [projects, setProjects] = useState<BulkProviderChangeProjectTableData[]>([]);
  const [resultsSummary, setResultsSummary] = useState<ResultsSummaryTableData[]>([]);
  const [reviewProjects, setReviewProjects] = useState<ReviewProjectsTableData[]>([]);
  const [page, setPage] = useState<BulkTargetingWizardSteps>(BulkTargetingWizardSteps.SelectProjects);
  const [eligibleSelectedProjectIds, setEligibleSelectedProjectIds] = useState<string[]>([]);
  const [totalSelectedProjectIds, setTotalSelectedProjectIds] = useState<string[]>([]);
  const [bulkProviderTargetId, setBulkProviderTargetId] = useState<number>();
  const [targetingReason, setTargetingReason] = useState<string>("");
  const [existingTargetAction, setExistingTargetAction] = useState<BulkTargetExistingTargetActionEnum>();
  const [isDistinctServicingProviderReport, setIsDistinctServicingProviderReport] = useState<boolean>(false);
  const [includeReportMetadataSummary, setIncludeReportMetadataSummary] = useState<boolean>(false);
  const [selectedReports, setSelectedReports] = useState<string[]>([]);
  const [columnConfigurations, setColumnConfigurations] = useState<{ [reportType: string]: ColumnConfigurationReport }>({});
  const [reportPresets, setReportPresetsState] = useState<Partial<Record<ReportType, ColumnConfigurationPresetOption[]>>>({});
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const setReportPresets = (reportType: ReportType, presets: ColumnConfigurationPresetOption[]) => {
    setReportPresetsState((prev) => ({ ...prev, [reportType]: presets }));
  };

  const availableReportSectionKeys = useMemo(() => {
    if (!isVolumeTargetingReportConfigurationEnabled) {
      return [];
    }

    return getTargetingReportSections(isDistinctServicingProviderReport, projectType).map(section => section.key);
  }, [isDistinctServicingProviderReport, isVolumeTargetingReportConfigurationEnabled, projectType]);

  useEffect(() => {
    if (isVolumeTargetingReportConfigurationEnabled && !isInitialized && availableReportSectionKeys.length > 0) {
      setSelectedReports(availableReportSectionKeys);
      setIsInitialized(true);
    }
  }, [availableReportSectionKeys, isInitialized, isVolumeTargetingReportConfigurationEnabled]);


  const value = {
    projectService,
    projectionProviderService,
    availableReportSectionKeys,
    projectType,

    bulkImportFormData,
    bulkProviderTargetId,
    comment,
    columnConfigurations,
    reportPresets,
    eligibleSelectedProjectIds,
    emailOnSuccess,
    existingTargetAction,
    filename,
    formatType,
    invalidItems,
    isDistinctServicingProviderReport,
    includeReportMetadataSummary,
    page,
    projects,
    resultsSummary,
    reviewProjects,
    selectedReports,
    targetingReason,
    totalSelectedProjectIds,

    setBulkImportFormData,
    setBulkProviderTargetId,
    setComment,
    setColumnConfigurations,
    setReportPresets,
    setEligibleSelectedProjectIds,
    setEmailOnSuccess,
    setExistingTargetAction,
    setFilename,
    setFormatType,
    setInvalidItems,
    setIsDistinctServicingProviderReport,
    setIncludeReportMetadataSummary,
    setPage,
    setProjects,
    setResultsSummary,
    setReviewProjects,
    setSelectedReports,
    setTargetingReason,
    setTotalSelectedProjectIds
  };

  return (
    <BulkTargetingWizardContext.Provider value={value}>
      {props.children}
    </BulkTargetingWizardContext.Provider>
  );

};

export { BulkTargetingWizardContext, BulkTargetingWizardProvider };
